import debugUtil from '../util/debug';
import * as plugins from '../plugins';

const Debug = debugUtil('PluginManager');

/**
 * Converts a string into camel case.
 * @see {@link https://stackoverflow.com/a/15829686} for origins.
 */
const camelize = (str) => str.replace(/^([A-Z])|[\s-_]+(\w)/g, (match, p1, p2) => {
	return p2 ? p2.toUpperCase() : p1.toLowerCase();
});

/**
 * Constructs a plugin object from a function.
 */
const createPluginFromFunction = (fn) => ({
	create: fn,
	destroy: () => null
});

/**
 * Searches the bundled plugins object, i.e. the plugins/index module, by
 * performing a string key lookup.
 */
const createPluginFromString = (pluginName) => plugins[camelize(pluginName)];

/**
 * Validates a plugin object, making sure it has a create function and a
 * destroy function.
 *
 * @param {object} plugin The plugin object to validate.
 * @param {string} pluginName String used to identify the plugin in error
 * messages.
 * @return {boolean} True if the plugin is valid.
 */
const validatePlugin = (plugin, pluginName) => {
	if (typeof plugin !== 'object') {
		Debug.error(`Plugin "${pluginName}" (${plugin}) is not a valid plugin.`);

		return false;
	}

	if (typeof plugin.create !== 'function') {
		Debug.error(`Plugin "${pluginName}" (${plugin}) does not contain a "create" function.`);

		return false;
	}

	if (typeof plugin.destroy !== 'function') {
		Debug.error(`Plugin "${pluginName}" (${plugin}) does not contain a "destroy" function`);

		return false;
	}

	return true;
};

/**
 * The PluginManager class maintain a collection of plugins. It provides
 * functions to manage the plugins lifecycles.
 */
class PluginManager {
	constructor() {
		this.plugins = [];
	}

	/**
	 * Adds a plugin to the manager.
	 *
	 * @param {string|function|object} param Plugin to add.
	 * @param {boolean} [prepend] If true, the plugin is added to the beginning
	 * of the internal plugin collection.
	 */
	addPlugin(plugin, prepend = false) {
		let pluginObject;
		let pluginName;

		switch (typeof plugin) {
			case 'function':
				pluginObject = createPluginFromFunction(plugin);
				pluginName = plugin.name || 'function';
				break;
			case 'string':
				pluginObject = createPluginFromString(plugin);
				pluginName = plugin;
				break;
			case 'object':
				pluginObject = plugin;
				pluginName = plugin.namespace || 'object';
				break;
			default:
				pluginObject = plugin;
				pluginName = 'unknown';
				break;
		}

		if (validatePlugin(pluginObject, pluginName)) {
			if (prepend) {
				this.plugins.unshift(pluginObject);
			} else {
				this.plugins.push(pluginObject);
			}

			Debug.log(`Loaded plugin "${pluginName}"`);
		}
	}

	/**
	 * Invokes the create function on all plugins.
	 */
	createAll(api) {
		this.plugins.forEach((plugin) => plugin.create(api));
	}

	/**
	 * Invokes the destroy function on all plugins.
	 */
	destroyAll(api) {
		this.plugins.forEach((plugin) => plugin.destroy(api));
	}
}

module.exports = PluginManager;




// WEBPACK FOOTER //
// ./src/js/modules/plugin-manager.js